import { z } from 'zod';
import { BASE_DATA_STATUSES } from '../property-data.js';
/**
 * „Objekt-Vorgaben" of a property (migration 0053, Nico Kremer's feedback 01.11.2026, owner decision 02.11.2026,
 * D-358…D-363, docs/dev/api-properties.md „Calculation defaults"): the eight calculation defaults the investment
 * calculator inherits between the unit and the organization, and the property's sales facts. Read on
 * `PropertyRecord.calcDefaults`, written through `PATCH /properties/:id` `{ calcDefaults: { … } }` (partial; null
 * clears a value). None of the fields is managed by TeamProQ. Ranges mirror the CHECK `property_calc_defaults_range`
 * and the column types (`numeric(5,2)`, `integer` cents, `bigint` cents, `numeric(10,2)` m²).
 */
/** „Gebäudeanteil" the calculator uses when neither the unit nor the property has one (no organization setting). */
export const CALCULATOR_STANDARD_BUILDING_SHARE_PCT = 80;
/** The eight calculation fields — the inherited ones; `calcDefaultsStatus` and the base data count them. */
export const PROPERTY_CALC_KEYS = [
    'depreciationPct',
    'buildingSharePct',
    'interestPct',
    'repaymentPct',
    'sevCents',
    'wegCents',
    'reservePerSqmCents',
    'otherCostsPerSqmCents',
];
/** Sales facts of the sheet's group „Vertrieb" and the storyline (never inherited). */
export const PROPERTY_SALES_KEYS = ['priceFromCents', 'yieldFromPct', 'landAreaSqm', 'storyline'];
/** Labels and suffixes of the card „Objekt-Vorgaben", its sheet and the base-data group „Kalkulation". */
export const PROPERTY_CALC_FIELDS = {
    depreciationPct: { key: 'depreciationPct', label: 'AfA-Satz p. a.', suffix: '%', kind: 'percent' },
    buildingSharePct: { key: 'buildingSharePct', label: 'Gebäudeanteil', suffix: '%', kind: 'percent' },
    interestPct: { key: 'interestPct', label: 'Sollzins p. a.', suffix: '%', kind: 'percent' },
    repaymentPct: { key: 'repaymentPct', label: 'Tilgung p. a.', suffix: '%', kind: 'percent' },
    sevCents: { key: 'sevCents', label: 'SE-Verwaltung', suffix: '€/Mon.', kind: 'amount' },
    wegCents: { key: 'wegCents', label: 'WEG-Verwaltung', suffix: '€/Mon.', kind: 'amount' },
    reservePerSqmCents: {
        key: 'reservePerSqmCents',
        label: 'Instandhaltungsrücklage',
        suffix: '€/m² mtl.',
        kind: 'amount',
    },
    otherCostsPerSqmCents: {
        key: 'otherCostsPerSqmCents',
        label: 'Sonstige nicht umlagefähige Kosten',
        suffix: '€/m² mtl.',
        kind: 'amount',
    },
    priceFromCents: { key: 'priceFromCents', label: 'Kaufpreise ab', suffix: '€', kind: 'amount' },
    yieldFromPct: { key: 'yieldFromPct', label: 'Rendite ab', suffix: '%', kind: 'percent' },
    landAreaSqm: { key: 'landAreaSqm', label: 'Grundstücksfläche', suffix: 'm²', kind: 'area' },
    storyline: { key: 'storyline', label: 'Objektbeschreibung für den Vertrieb', suffix: '', kind: 'text' },
};
/** complete = all eight calculation values, partial = at least one, empty = none (labels `baseDataStatusLabels`). */
export function propertyCalcStatus(values) {
    const set = PROPERTY_CALC_KEYS.filter((key) => values[key] !== null).length;
    return set === PROPERTY_CALC_KEYS.length ? 'complete' : set > 0 ? 'partial' : 'empty';
}
const TWO_DECIMALS_MESSAGE = 'Höchstens zwei Nachkommastellen.';
const twoDecimals = (value) => Math.abs(Math.round(value * 100) - value * 100) < 1e-6;
/** Upper bound of an `integer` column. */
const INT_MAX = 2_147_483_647;
const percentUpTo = (max, message) => z
    .number({ message: 'Bitte tragen Sie eine Zahl ein.' })
    .min(0, { message })
    .max(max, { message })
    .refine(twoDecimals, { message: TWO_DECIMALS_MESSAGE })
    .nullable();
const amount = (max) => z
    .number({ message: 'Bitte tragen Sie einen Betrag ein.' })
    .int({ message: 'Bitte tragen Sie einen Betrag in Euro und Cent ein.' })
    .min(0, { message: 'Der Betrag darf nicht negativ sein.' })
    .max(max, { message: 'Der Betrag ist zu groß.' })
    .nullable();
const PERCENT_MESSAGE = 'Bitte einen Wert zwischen 0 und 100 % angeben.';
/** `PATCH /properties/:id` `calcDefaults` (partial; shared by the API and the SPA). */
export const propertyCalcDefaultsBodySchema = z
    .object({
    depreciationPct: percentUpTo(10, 'Bitte einen AfA-Satz zwischen 0 und 10 % angeben.'),
    buildingSharePct: percentUpTo(100, PERCENT_MESSAGE),
    interestPct: percentUpTo(100, PERCENT_MESSAGE),
    repaymentPct: percentUpTo(100, PERCENT_MESSAGE),
    sevCents: amount(INT_MAX),
    wegCents: amount(INT_MAX),
    reservePerSqmCents: amount(INT_MAX),
    otherCostsPerSqmCents: amount(INT_MAX),
    priceFromCents: amount(Number.MAX_SAFE_INTEGER),
    yieldFromPct: percentUpTo(100, PERCENT_MESSAGE),
    landAreaSqm: z
        .number({ message: 'Bitte tragen Sie eine Zahl ein.' })
        .min(0, { message: 'Die Fläche darf nicht negativ sein.' })
        .max(99_999_999, { message: 'Die Fläche ist zu groß.' })
        .refine(twoDecimals, { message: TWO_DECIMALS_MESSAGE })
        .nullable(),
    storyline: z
        .string()
        .trim()
        .max(20_000, { message: 'Höchstens 20.000 Zeichen.' })
        .transform((value) => (value === '' ? null : value))
        .nullable(),
})
    .partial()
    .strict();
/** `PropertyRecord.calcDefaults`. */
export const propertyCalcDefaultsSchema = z.object({
    depreciationPct: z.number().nullable(),
    buildingSharePct: z.number().nullable(),
    interestPct: z.number().nullable(),
    repaymentPct: z.number().nullable(),
    sevCents: z.number().int().nullable(),
    wegCents: z.number().int().nullable(),
    reservePerSqmCents: z.number().int().nullable(),
    otherCostsPerSqmCents: z.number().int().nullable(),
    priceFromCents: z.number().int().nullable(),
    yieldFromPct: z.number().nullable(),
    landAreaSqm: z.number().nullable(),
    storyline: z.string().nullable(),
    /** Over the eight calculation values. */
    status: z.enum(BASE_DATA_STATUSES),
});
//# sourceMappingURL=properties.js.map